import { addDays, diffDays, toISODate } from './dates';

/**
 * Documentos legais (Política de Privacidade, Termos de Uso e Política de Cookies), as
 * versões, o aceite e o catálogo do que fica guardado no navegador.
 *
 * **Os textos são um modelo e precisam de revisão jurídica antes de publicar.** O que é
 * provisório (o e-mail do encarregado, o CNPJ, o foro, o histórico de versões…) está em
 * `LEGAL_PENDING_REVIEW` e no `LEGAL.md` da raiz do repositório.
 *
 * Papéis na LGPD: a **igreja** (a organização) é a controladora — decide o que é tratado
 * e para quê; a **Live Gospel** é a operadora — trata em nome dela, para o serviço
 * funcionar.
 */

export type LegalDocKey = 'privacy' | 'terms' | 'cookies';

/**
 * Versão dos Termos de Uso e da Política de Privacidade. A aplicação ainda não foi
 * lançada: a 1.0 é a primeira. **Subir a versão pede novo aceite de todos** — só quando o
 * texto muda o que é tratado, para quê, com quem ou os direitos da pessoa (correção de
 * redação não sobe). Ao subir: troque `LEGAL_UPDATED_AT`, escreva `LEGAL_CHANGES` (o que
 * mudou, mostrado no pedido de novo aceite) e registre no histórico do `LEGAL.md`. O
 * fluxo de lançamento de versões vai organizar isso.
 */
export const LEGAL_VERSION = '1.0';
/** Data da versão atual (`yyyy-mm-dd`). */
export const LEGAL_UPDATED_AT = '2026-10-15';

/**
 * O que mudou da versão anterior, mostrado em "Atualizamos nossa Política de
 * Privacidade". Vazio na primeira versão (não há anterior).
 */
export const LEGAL_CHANGES: readonly string[] = [];

/** O essencial dos documentos, mostrado no primeiro aceite (conta que nunca aceitou). */
export const LEGAL_HIGHLIGHTS: readonly string[] = [
  'Sua participação na igreja (situação, batismo, ministérios) é um dado sensível e só é tratada com o seu consentimento.',
  'A Política de Cookies lista tudo o que fica no seu navegador, e você escolhe o que aceita.',
  'Você pode baixar uma cópia dos seus dados, corrigir o cadastro e pedir a exclusão pelo próprio app, em “Privacidade e dados”.',
  'Você escolhe por quais canais quer receber lembretes, cobranças e avisos de mudança na sua escala (e-mail, WhatsApp, notificações).',
];

/** Contato do encarregado (DPO) e da autoridade. */
export const PRIVACY_CONTACT = {
  /** Fictício até a definição do encarregado (ver `LEGAL_PENDING_REVIEW`). */
  email: 'privacidade@livegospel.app',
  anpd: 'https://www.gov.br/anpd',
} as const;

/** Prazo para responder ao pedido do titular, em dias (LGPD, art. 19, II). */
export const PRIVACY_DEADLINE_DAYS = 15;

/**
 * O que precisa de revisão (jurídica ou de negócio) antes de publicar os documentos. Fica
 * no código para os testes travarem que a lista existe enquanto houver texto provisório.
 */
export const LEGAL_PENDING_REVIEW: readonly string[] = [
  'Revisão jurídica de todos os textos (Privacidade, Termos e Cookies).',
  'Razão social, CNPJ e endereço da Live Gospel (operadora).',
  'Encarregado (DPO): nome e e-mail reais — privacidade@livegospel.app é fictício.',
  'Base legal da participação religiosa (dado sensível): consentimento (art. 11, I) ou outra hipótese.',
  'Base legal do CPF (hoje: legítimo interesse, art. 7º, IX, só para não duplicar o cadastro) e se a igreja precisa dele para outra finalidade (recibo, emissão de documento).',
  'Cadastro de menores de idade: consentimento de um dos pais ou responsável (art. 14).',
  'Geocodificação: o Nominatim público do OpenStreetMap aceita no máximo 1 consulta por segundo e não é para uso intenso; com muitas igrejas, contratar um serviço ou subir um Nominatim próprio (`GEOCODER_URL`) e rever a transferência internacional.',
  'Mapa-base da Distribuição geográfica: o navegador do Administrador baixa o mapa do OpenFreeMap (dados do OpenStreetMap), que vê o endereço IP e a região visualizada. O serviço gratuito não garante disponibilidade nem tem contrato: avaliar um provedor pago ou próprio (tiles) e confirmar onde ele guarda os registros de acesso.',
  'Destino do backup fora da VPS e onde a Hostinger (hospedagem e e-mail) guarda os dados (suboperadores e transferência internacional).',
  'Foro dos Termos de Uso.',
  'Contrato (ou cláusulas) de operador entre cada igreja e a Live Gospel.',
  'Fontes do Google: servir a Plus Jakarta Sans pelo próprio domínio, para o navegador não falar com o Google.',
];

export interface LegalSection {
  heading: string;
  paragraphs: string[];
  /** Lista depois dos parágrafos (opcional). */
  items?: string[];
}

export interface LegalDocument {
  key: LegalDocKey;
  title: string;
  /** Rótulo curto (aba). */
  short: string;
  version: string;
  updatedAt: string;
  sections: LegalSection[];
}

export const LEGAL_DOC_LABEL: Record<LegalDocKey, { title: string; short: string }> = {
  privacy: { title: 'Política de Privacidade', short: 'Privacidade' },
  terms: { title: 'Termos de Uso', short: 'Termos' },
  cookies: { title: 'Política de Cookies', short: 'Cookies' },
};

export const LEGAL_DOC_KEYS: readonly LegalDocKey[] = ['privacy', 'terms', 'cookies'];

/** A pessoa precisa aceitar (de novo) os Termos e a Política. */
export function needsTermsAcceptance(acceptedVersion: string | null): boolean {
  return acceptedVersion !== LEGAL_VERSION;
}

// --- Cookies e armazenamento local -----------------------------------------------------

export type CookieCategory = 'essential' | 'functional' | 'analytics';

export interface CookieEntry {
  /** Nome do cookie ou da chave (`escala:pwa:*` = um grupo). */
  name: string;
  kind: 'cookie' | 'local' | 'cache';
  purpose: string;
  duration: string;
}

export interface CookieCategoryInfo {
  key: CookieCategory;
  title: string;
  description: string;
  /** Essenciais não se desligam. */
  required: boolean;
  entries: CookieEntry[];
}

export const COOKIE_KIND_LABEL: Record<CookieEntry['kind'], string> = {
  cookie: 'Cookie',
  local: 'Armazenamento local',
  cache: 'Cache do aplicativo',
};

/**
 * Tudo o que o Live Gospel guarda no navegador. Entrou chave nova no client?
 * Ela entra aqui, na categoria certa — `storageCategory` decide o que se apaga quando a
 * pessoa desliga a categoria, e o teste do client trava as chaves conhecidas.
 */
export const COOKIE_CATEGORIES: readonly CookieCategoryInfo[] = [
  {
    key: 'essential',
    title: 'Essenciais',
    description:
      'Necessários para entrar, manter a sessão segura, abrir a igreja certa e lembrar as suas escolhas de cookies. Não podem ser desligados.',
    required: true,
    entries: [
      {
        name: 'escala_refresh',
        kind: 'cookie',
        purpose: 'Mantém você conectado com segurança (renova o acesso sem pedir a senha a cada 15 minutos).',
        duration: '30 dias com “Manter conectado”; senão, até fechar o navegador',
      },
      {
        name: 'escala:active-org',
        kind: 'local',
        purpose: 'A igreja aberta pelo Administrador da plataforma.',
        duration: 'Até sair ou trocar de igreja',
      },
      {
        name: 'escala:consent',
        kind: 'local',
        purpose: 'Guarda as suas escolhas de cookies.',
        duration: '12 meses',
      },
      {
        name: 'Arquivos do aplicativo',
        kind: 'cache',
        purpose: 'Telas, ícones e fontes, para o app abrir rápido. Sem nenhum dado pessoal.',
        duration: 'Até a próxima versão do app',
      },
    ],
  },
  {
    key: 'functional',
    title: 'Funcionais',
    description:
      'Lembram preferências neste navegador. Sem eles, tudo funciona, mas o tema e os avisos voltam ao padrão ao recarregar.',
    required: false,
    entries: [
      {
        name: 'escala:theme',
        kind: 'local',
        purpose: 'Tema claro ou escuro neste navegador (com a conta, o tema também fica salvo nela).',
        duration: 'Até você mudar',
      },
      {
        name: 'escala:org',
        kind: 'local',
        purpose: 'A última igreja escolhida no login.',
        duration: 'Até escolher outra',
      },
      {
        name: 'escala:pwa:*',
        kind: 'local',
        purpose: 'Se você dispensou ou já instalou o aplicativo no celular.',
        duration: 'Até limpar o navegador',
      },
    ],
  },
  {
    key: 'analytics',
    title: 'Análise',
    description:
      'Estatísticas de uso anônimas, para entender o que melhorar. Hoje nenhuma ferramenta de análise está ligada: se passar a existir, ela só funciona com esta opção ligada e aparece aqui.',
    required: false,
    entries: [],
  },
];

/** Versão do catálogo de cookies: mudou (categoria ou cookie novo) → perguntar de novo. */
export const COOKIE_POLICY_VERSION = '1.0';
/** A escolha vale por 12 meses; depois, o aviso volta. */
export const CONSENT_MAX_AGE_DAYS = 365;

export interface CookieConsent {
  version: string;
  functional: boolean;
  analytics: boolean;
  /** Quando a pessoa escolheu (ISO 8601). */
  decidedAt: string;
}

export type CookieChoice = 'all' | 'essential' | { functional: boolean; analytics: boolean };

export function cookieConsent(choice: CookieChoice, now: Date): CookieConsent {
  const all = choice === 'all';
  const picked = typeof choice === 'object' ? choice : { functional: all, analytics: all };

  return {
    version: COOKIE_POLICY_VERSION,
    functional: picked.functional,
    analytics: picked.analytics,
    decidedAt: now.toISOString(),
  };
}

/** A escolha guardada ainda vale: mesma versão do catálogo e menos de 12 meses. */
export function consentIsCurrent(consent: CookieConsent | null, now: Date): consent is CookieConsent {
  if (!consent || consent.version !== COOKIE_POLICY_VERSION) return false;

  const decided = toISODate(new Date(consent.decidedAt));

  return diffDays(decided, toISODate(now)) < CONSENT_MAX_AGE_DAYS;
}

/** Lê a escolha guardada (texto JSON), recusando o que não tem o formato. */
export function parseConsent(raw: string | null): CookieConsent | null {
  if (!raw) return null;

  try {
    const value: unknown = JSON.parse(raw);

    if (
      typeof value === 'object' &&
      value !== null &&
      'version' in value &&
      typeof value.version === 'string' &&
      'functional' in value &&
      typeof value.functional === 'boolean' &&
      'analytics' in value &&
      typeof value.analytics === 'boolean' &&
      'decidedAt' in value &&
      typeof value.decidedAt === 'string' &&
      !Number.isNaN(Date.parse(value.decidedAt))
    ) {
      return {
        version: value.version,
        functional: value.functional,
        analytics: value.analytics,
        decidedAt: value.decidedAt,
      };
    }
  } catch {
    // Texto corrompido: como se não houvesse escolha.
  }

  return null;
}

export function allowsCategory(consent: CookieConsent | null, category: CookieCategory): boolean {
  if (category === 'essential') return true;
  if (!consent) return false;

  return consent[category];
}

/** "Essenciais e funcionais", "Todos", "Só essenciais". */
export function consentSummary(consent: CookieConsent | null): string {
  if (!consent) return 'Nenhuma escolha feita';
  if (consent.functional && consent.analytics) return 'Todos aceitos';
  if (consent.functional) return 'Essenciais e funcionais';
  if (consent.analytics) return 'Essenciais e análise';

  return 'Só essenciais';
}

/** A categoria de uma chave do `localStorage` (o `*` do catálogo casa com o prefixo). */
export function storageCategory(key: string): CookieCategory | null {
  for (const category of COOKIE_CATEGORIES) {
    for (const entry of category.entries) {
      if (entry.kind !== 'local') continue;

      const matches = entry.name.endsWith('*') ? key.startsWith(entry.name.slice(0, -1)) : key === entry.name;

      if (matches) return category.key;
    }
  }

  return null;
}

// --- Os textos ---------------------------------------------------------------------------

export interface LegalContext {
  /** A igreja (controladora); sem ela, o texto fala em "a sua igreja". */
  orgName?: string;
}

function church(ctx: LegalContext): string {
  return ctx.orgName ? `a ${ctx.orgName}` : 'a sua igreja';
}

function privacySections(ctx: LegalContext): LegalSection[] {
  const org = church(ctx);

  return [
    {
      heading: 'Quem cuida dos seus dados',
      paragraphs: [
        `O Live Gospel organiza as escalas de serviço da igreja. Quem decide quais dados são usados e para quê é ${org} — a controladora, na linguagem da Lei Geral de Proteção de Dados (LGPD, Lei 13.709/2018).`,
        'A Live Gospel fornece o aplicativo e trata os dados em nome da igreja, só para o serviço funcionar — é a operadora. Não usamos os seus dados para fins próprios, não vendemos e não fazemos publicidade com eles.',
      ],
    },
    {
      heading: 'Quais dados tratamos',
      paragraphs: ['Só o necessário para organizar as escalas e falar com você:'],
      items: [
        'Cadastro: nome, nome de apresentação (o apelido ou o nome do crachá), sexo, data de nascimento, e-mail, telefone (WhatsApp), estado civil, profissão, cônjuge e, se você enviar, a foto do perfil (também pelo formulário de cadastro, para a secretaria reconhecer você; recusado o cadastro, a foto é apagada).',
        'CPF (opcional), endereço (e as coordenadas obtidas dele, para o mapa) e contato de emergência — vistos só por você e pela administração da igreja.',
        'Vida na igreja: situação, desde quando é membro, como entrou, batismo e igreja anterior.',
        'Serviço: ministérios e funções, disponibilidade, ausências, escalas, confirmações de presença, pedidos de troca e check-in.',
        'Público dos eventos: só números (presentes, membros, visitantes, crianças, online), sem identificar quem esteve presente, e o nome de quem fez o registro.',
        'Ficha de visitante (opcional, só com autorização de quem visitou): nome, celular ou e-mail, faixa etária, bairro, como conheceu a igreja, as datas das visitas, se quer receber contato ou conhecer a membresia e os acompanhamentos da equipe (prazo, canal e resultado do contato). Não pedimos motivo da visita, pedido de oração nem outra informação sensível.',
        'Acesso: e-mail de login, senha (guardada só como código irreversível), datas de acesso e as sessões abertas.',
        'Link para completar o cadastro: quando a igreja envia, um link pessoal (válido por 30 dias) em que você mesmo confere e completa os seus dados, com a data em que salvou e a sua autorização. Por segurança, ele não mostra o CPF, o endereço nem os contatos já guardados.',
        'Preferências: tema, canais de comunicação, escolhas de cookies e o aceite destes documentos.',
      ],
    },
    {
      heading: 'Dados sensíveis: a sua participação na igreja',
      paragraphs: [
        'Saber que você é membro de uma igreja, em quais ministérios serve e quando foi batizado revela a sua convicção religiosa — um dado sensível para a LGPD (art. 5º, II). Por isso ele só é tratado com o seu consentimento específico e destacado (art. 11, I), dado ao criar a senha ou ao enviar o cadastro, e você pode revogá-lo quando quiser.',
        'Esses dados ficam restritos à sua igreja: outras igrejas que usam o Live Gospel não os veem.',
      ],
    },
    {
      heading: 'Para que usamos e com que base legal',
      paragraphs: [],
      items: [
        'Montar as escalas, avisar quem serve e registrar a presença — consentimento (art. 7º, I, e art. 11, I).',
        'Criar e proteger a sua conta (login, senha, sessões) — execução do serviço que você pediu (art. 7º, V) e legítimo interesse em segurança (art. 7º, IX).',
        'Identificar o seu cadastro pelo CPF, quando informado, para a igreja não ter dois cadastros da mesma pessoa — legítimo interesse da igreja em manter o cadastro correto (art. 7º, IX). O CPF é opcional, não é usado para mais nada e não é compartilhado.',
        'Indicadores da comunidade (Membresia): contagens agregadas de membros por período, faixa etária, situação na igreja e bairro, e o público dos eventos, para a liderança acompanhar a igreja — o mesmo consentimento do cadastro (art. 7º, I, e art. 11, I). Ninguém é identificado nesses números: bairro com menos de 5 pessoas aparece como "<5", e a contagem por bairro, que usa o endereço, só a administração vê.',
        'Acompanhar quem visitou a igreja (boas-vindas, retorno do contato e, se a pessoa quiser, o cadastro de membro) — consentimento de quem preencheu a ficha ou autorizou a equipe a registrá-la (art. 7º, I). Só a administração e a liderança do acolhimento veem a ficha; os demais líderes veem apenas números. As etapas do acompanhamento são administrativas e não avaliam a fé de ninguém.',
        'Lembrar a liderança dos aniversários (Membresia › Aniversariantes, o Dashboard e o aviso do dia), com a data de nascimento do cadastro, para a igreja dar os parabéns — inclusive pelo WhatsApp, que a liderança abre no próprio celular — e dar os parabéns a você no seu dia — o mesmo consentimento do cadastro (art. 7º, I). A lista mostra o dia e a idade que você completa; o telefone só aparece para a administração e para quem lidera o ministério em que você serve.',
        'Lembretes, cobranças de confirmação e avisos de mudança na sua escala (quando você sai, muda de função ou entra numa escala já publicada) — legítimo interesse da igreja em organizar o serviço; você pode desligar cada canal.',
        'Guardar registros de acesso pelo prazo da lei — cumprimento de obrigação legal (art. 7º, II; Marco Civil da Internet, art. 15).',
      ],
    },
    {
      heading: 'Quem vê e com quem compartilhamos',
      paragraphs: [
        'Dentro da igreja, cada pessoa vê só o que precisa: os líderes veem a escala e a disponibilidade de quem lideram; CPF, endereço e contato de emergência, só você e a administração. A foto do perfil, quando enviada, aparece para as pessoas da igreja nas escalas; nas imagens e PDFs da escala exportados, só quando quem exporta escolhe incluir as fotos (o padrão são as iniciais). Só você e a administração trocam ou removem a foto.',
        'Para o serviço funcionar, usamos fornecedores que tratam os dados em nosso nome, com as mesmas obrigações de sigilo: a hospedagem dos servidores e o envio de e-mails (Hostinger). Quando você escolhe, os dados também vão para serviços de terceiros: a sua agenda (Google, Apple, Outlook) ao assinar as suas escalas, o WhatsApp ao abrir uma mensagem e o ViaCEP, que recebe só o CEP digitado para preencher o endereço. Para localizar o endereço no mapa da Membresia, o servidor envia só o endereço (rua, número, bairro, cidade e CEP — nunca o nome) ao Nominatim, o serviço de busca do OpenStreetMap. Para desenhar o mapa da Distribuição geográfica, o navegador do Administrador baixa o mapa-base (imagens e nomes de ruas do OpenStreetMap) do OpenFreeMap, que vê o endereço IP e a região mostrada, mas nenhum dado de membro.',
        'Podemos ser obrigados a entregar dados por ordem judicial ou de autoridade competente.',
      ],
    },
    {
      heading: 'Transferência internacional',
      paragraphs: [
        'Alguns fornecedores podem guardar ou processar dados fora do Brasil — o Nominatim (OpenStreetMap), que localiza os endereços, fica na Europa. Quando isso acontece, exigimos as garantias do art. 33 da LGPD (como cláusulas contratuais aprovadas pela ANPD).',
      ],
    },
    {
      heading: 'Por quanto tempo guardamos',
      paragraphs: [
        'Enquanto você fizer parte da igreja no Live Gospel. Pedida a exclusão, a igreja anonimiza o seu cadastro em até 15 dias: o nome, a foto, o CPF e os contatos somem, e o histórico de escalas fica só como estatística, sem identificar você.',
        'A ficha de visitante é anonimizada automaticamente depois de 12 meses (ou o prazo que a igreja definir, entre 3 e 24) sem visita nem contato, e antes disso se a pessoa pedir ou disser que não quer mais contato. Ficam só as datas e as etapas, para a estatística.',
        'As cópias de segurança são apagadas em até 14 dias e os registros de acesso ficam por 6 meses, como pede a lei. Dados que a lei exigir manter são preservados pelo prazo legal.',
      ],
    },
    {
      heading: 'Como protegemos',
      paragraphs: [
        'Conexão sempre criptografada (HTTPS), senha guardada só como código irreversível, sessão que se renova e pode ser encerrada em todos os aparelhos, isolamento entre igrejas e cópias de segurança diárias. Se houver um incidente que possa trazer risco a você, a igreja e a ANPD serão avisadas.',
      ],
    },
    {
      heading: 'Seus direitos',
      paragraphs: [
        'Pela LGPD (art. 18), você pode, a qualquer momento: confirmar se tratamos os seus dados e ter acesso a eles; corrigir o que estiver incompleto ou errado; pedir a anonimização, o bloqueio ou a eliminação do que for desnecessário; levar os dados para outro serviço (portabilidade); saber com quem foram compartilhados; e revogar o consentimento.',
        'No app, em “Privacidade e dados” (no menu com o seu nome), você baixa uma cópia dos seus dados, corrige o cadastro e envia os pedidos. Se a igreja inativar o seu cadastro, o acesso é encerrado, mas ao tentar entrar você ainda pode pedir a exclusão dos seus dados. A igreja responde em até 15 dias.',
      ],
    },
    {
      heading: 'Encarregado e contato',
      paragraphs: [
        `Fale com o encarregado de dados (DPO) pelo e-mail ${PRIVACY_CONTACT.email}. Você também pode procurar a Autoridade Nacional de Proteção de Dados (ANPD), em ${PRIVACY_CONTACT.anpd}.`,
      ],
    },
    {
      heading: 'Crianças e adolescentes',
      paragraphs: [
        'O cadastro de menores de 18 anos só pode ser feito com o consentimento de um dos pais ou do responsável legal, sempre no melhor interesse da criança ou do adolescente (art. 14).',
        'O formulário público da ficha de visitante é só para quem tem 18 anos ou mais; crianças e adolescentes que visitam a igreja entram apenas na contagem do evento.',
      ],
    },
    {
      heading: 'Mudanças nesta política',
      paragraphs: [
        'Quando esta política mudar, a versão nova aparece logo depois do login, com o resumo do que mudou, e pedimos o seu aceite de novo.',
      ],
    },
  ];
}

function termsSections(ctx: LegalContext): LegalSection[] {
  const org = church(ctx);

  return [
    {
      heading: 'Aceite',
      paragraphs: [
        'Estes Termos valem para quem usa o Live Gospel. Ao criar a senha ou entrar, você concorda com eles e com a Política de Privacidade. Se não concordar, não use o aplicativo e fale com a administração da sua igreja.',
      ],
    },
    {
      heading: 'O serviço',
      paragraphs: [
        'O Live Gospel ajuda a igreja a organizar eventos, ministérios, funções, a disponibilidade dos membros e as escalas, inclusive com geração automática. Cada igreja configura o próprio espaço e decide quem tem acesso a ele.',
      ],
    },
    {
      heading: 'Sua conta',
      paragraphs: [
        'O acesso é pessoal: não compartilhe a sua senha. Avise a administração se perceber um acesso que não reconhece — você também pode encerrar as sessões dos outros aparelhos em “Conta e senha”.',
      ],
    },
    {
      heading: 'Responsabilidades da igreja',
      paragraphs: [
        `${org.charAt(0).toUpperCase()}${org.slice(1)} é responsável pelos dados que cadastra: informar os membros, pedir o consentimento quando necessário, manter os dados corretos, dar acesso só a quem precisa e responder aos pedidos dos titulares no prazo.`,
      ],
    },
    {
      heading: 'Uso adequado',
      paragraphs: ['Não é permitido:'],
      items: [
        'cadastrar pessoas sem o conhecimento delas ou dados que não são necessários para as escalas;',
        'tentar acessar dados de outra igreja ou de perfis que você não tem;',
        'usar o aplicativo para enviar mensagens em massa que não sejam das escalas;',
        'atrapalhar o funcionamento do serviço ou tentar burlar a segurança.',
      ],
    },
    {
      heading: 'Disponibilidade',
      paragraphs: [
        'Trabalhamos para o aplicativo estar sempre no ar, mas pode haver pausas para manutenção ou por falhas fora do nosso controle. Avisos por e-mail e WhatsApp dependem de serviços de terceiros e podem atrasar.',
      ],
    },
    {
      heading: 'Encerramento',
      paragraphs: [
        'Você pode pedir a exclusão da sua conta a qualquer momento, em “Privacidade e dados”. A igreja pode remover o seu acesso; a Live Gospel pode suspender quem descumprir estes Termos.',
      ],
    },
    {
      heading: 'Responsabilidade',
      paragraphs: [
        'A Live Gospel responde pelo funcionamento do aplicativo nos limites da lei. As decisões tomadas a partir das escalas (quem serve, quando) são da igreja.',
      ],
    },
    {
      heading: 'Mudanças e lei aplicável',
      paragraphs: [
        'Quando estes Termos mudarem, avisamos no aplicativo e pedimos um novo aceite. Valem as leis do Brasil. O foro será definido na revisão jurídica destes Termos.',
      ],
    },
  ];
}

function cookieSections(): LegalSection[] {
  return [
    {
      heading: 'O que são',
      paragraphs: [
        'Cookies e o armazenamento local são pequenos registros que o navegador guarda para o site lembrar de algo entre uma visita e outra. O Live Gospel usa poucos: a lista completa, com o motivo e a duração de cada um, está abaixo.',
      ],
    },
    {
      heading: 'Como escolher',
      paragraphs: [
        'No primeiro acesso, você escolhe entre aceitar todos, ficar só com os essenciais ou personalizar. Nada além dos essenciais é usado antes da sua escolha. Você muda quando quiser em “Gerenciar cookies” (no rodapé do login e em “Privacidade e dados”); a escolha vale por 12 meses neste navegador.',
        'Desligar os funcionais apaga na hora o que eles tinham guardado.',
      ],
    },
    {
      heading: 'Serviços de terceiros',
      paragraphs: [
        'As fontes do aplicativo vêm do Google Fonts: ao abrir o app, o navegador as baixa dos servidores do Google, que recebem o seu endereço IP. Não usamos cookies de publicidade nem de redes sociais.',
      ],
    },
  ];
}

export function legalDocument(key: LegalDocKey, ctx: LegalContext = {}): LegalDocument {
  const sections = { privacy: privacySections, terms: termsSections, cookies: cookieSections }[key](ctx);

  return {
    key,
    ...LEGAL_DOC_LABEL[key],
    version: key === 'cookies' ? COOKIE_POLICY_VERSION : LEGAL_VERSION,
    updatedAt: LEGAL_UPDATED_AT,
    sections,
  };
}

/** Prazo do pedido do titular: 15 dias depois do envio. */
export function privacyDueDate(sentOn: string): string {
  return addDays(sentOn, PRIVACY_DEADLINE_DAYS);
}
